/**
 * MIXED-BUCKET HELPERS
 *
 * The backend endpoint (GetAssetResourcesDetails / AssetResourcesDetails) is a
 * "god endpoint" that returns Manuals/Documents, FAQs, Maintenance Guides AND
 * Notes all in the same response, discriminated by inconsistent combinations of
 * `name`, `type`, `Type` and `documentType` fields. Until the backend splits
 * the read path by EntityType/Id, this module acts as the adapter between that
 * toxic response shape and the clean typed interfaces the UI consumes. All
 * mixed-bucket parsing and filtering must live here — one source of truth.
 *
 * TODO: Remove this adapter layer once backend splits reads by EntityType/Id.
 */

/**
 * Parses a single mixed-bucket entity's stringified `data` blob and merges its
 * fields onto the entity. Notes, FAQs, and documents all carry their payload
 * inside a JSON-encoded `data` field (legacy BOS shape).
 */
export const transformFaqsNotesDocumentsEntity = (entity: any): any => {
  try {
    const parsedData: any = JSON.parse(entity.data);

    return {
      id: entity.id,
      documentType: entity.name,
      createdBy: entity.createdBy,
      itemName: entity.itemName,
      itemType: entity.itemType,
      faqType: entity.faqType,
      ...parsedData,
      data: entity?.data,
      reminderDate: entity?.reminderDate,
      time: entity?.time,
      IsSuiteWarrantyStartDate: entity?.isSuiteWarrantyStartDate,
    };
  } catch (error) {
    console.warn('Failed to parse entity data:', entity);
    return entity;
  }
};

export const getEntitiesByType = (
  faqsNotesDocuments: any,
  entityType: any
): any[] => {
  const filteredDocs: any = faqsNotesDocuments.filter((item: any) => {
    const currentItem: any = item?.type?.toLowerCase();
    return currentItem === entityType.toLowerCase();
  });

  return filteredDocs.map(transformFaqsNotesDocumentsEntity);
};

/**
 * Applies legacy BOS note-visibility policy:
 * - Admin / Super Admin / Spark Admin / Property Developer see every note
 *   (global / peer read). Edit-scope is gated separately in NotesRepeater.
 * - Home Owner / Realtor / Occupant see only notes explicitly marked
 *   `noteVisibleToHomeownerOccupantRealtor === true`.
 * - All other roles see notes whose `visibilityType` is null, 'Everyone' or
 *   their own role, plus any note they authored themselves.
 *
 * This is the canonical note-visibility function for the app. It previously
 * lived in a Redux selector (`selectNotes`); callers should use this instead.
 */
export const applyNotesVisibilityFilter = (
  notesFaqsDocsUnfiltered: any[],
  user: any
): any[] => {
  if (notesFaqsDocsUnfiltered.length === 0) {
    return [];
  }

  // legacy/workaround: god-endpoint can deliver FAQs/documents in the same
  // bucket as notes — strip non-notes before any role-based branching so the
  // admin/peer-read early-return doesn't leak non-notes through.
  const notes: any[] = notesFaqsDocsUnfiltered.filter(
    (item: any) => item.documentType === 'NOTES'
  );

  if (notes.length === 0) {
    return [];
  }

  if (!user?.roles?.[0]?.role?.name) {
    return notes;
  }

  const userRole: string = user.roles[0].role.name;

  if (
    userRole === 'Spark Admin' ||
    userRole === 'Admin' ||
    userRole === 'Super Admin' ||
    userRole === 'Property Developer' ||
    userRole === 'Service Provider'
  ) {
    return notes;
  }

  if (
    userRole === 'Home Owner' ||
    userRole === 'Realtor' ||
    userRole === 'Occupant'
  ) {
    return notes.filter(
      (note: any) => note.noteVisibleToHomeownerOccupantRealtor === true
    );
  }

  return notes.filter((note: any) => {
    const isPublicNote: boolean =
      note.visibilityType === null || note.visibilityType === 'Everyone';

    const isRoleSpecificNote: boolean = note.visibilityType === userRole;

    const isOwnNote: boolean =
      note.visibilityType !== userRole && note.author?.id === user.id;

    return isPublicNote || isRoleSpecificNote || isOwnNote;
  });
};

export const hasHomeOwnerVisibleNote = (notes: any[]): boolean => {
  if (!Array.isArray(notes)) return false;

  return notes.some(
    (note: any) => note?.noteVisibleToHomeownerOccupantRealtor === true
  );
};

export const filterOutNonDocuments = (rawDocs: any[]): any[] => {
  if (!Array.isArray(rawDocs)) return [];

  return rawDocs.filter(
    (doc: any) =>
      doc.name !== 'Frequently Asked Questions' &&
      doc.documentType !== 'Frequently Asked Questions' &&
      doc.Type !== 'FAQS' &&
      doc.name !== 'MAINTENANCEGUIDE' &&
      doc.type !== 'MAINTENANCEGUIDE' &&
      doc.name !== 'NOTES' &&
      doc.type?.toLowerCase() !== 'notes'
  );
};
